// @ts-nocheck
/* eslint-disable spire/export-styles */
import getLocalizedCurrency from "@insite/client-framework/Common/Utilities/getLocalizedCurrency";
import { getUnitListPrice, getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React, { FC } from "react";
import { connect } from "react-redux";

/* Store Custom Code - Start */
import { visuallyHiddenStyles } from "@toolkit/Themes/common/mixins";
import { css } from "styled-components";
/* Store Custom Code - End */

type OwnProps = TypographyPresentationProps & {
    pricing: ProductPriceDto;
    showSavingsAmount: boolean;
    showSavingsPercent: boolean;
    currencySymbol: string;
    qtyOrdered?: number;
};

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

/* Store Custom Code - Start */
export interface ProductPriceSavingsMessageStyles {
    regularPrice?: TypographyPresentationProps;
    priceStrikethrough?: TypographyPresentationProps;
    saveMsg?: TypographyPresentationProps;
}

export const productPriceSavingsMessageStylesStyles: ProductPriceSavingsMessageStyles = {
    regularPrice: {
        css: visuallyHiddenStyles(),
    },
    priceStrikethrough: {
        css: css`
            text-decoration: line-through;
        `,
    },
    saveMsg: {
        color: "text.salePrice",
    },
};

const styles = productPriceSavingsMessageStylesStyles;
/* Store Custom Code - End */

const ProductPriceSavingsMessage: FC<Props> = ({
    pricing,
    showSavingsAmount,
    showSavingsPercent,
    currencySymbol,
    qtyOrdered = 1,
    language,
    ...otherProps
}) => {
    const { price: unitNetPrice } = getUnitNetPrice(pricing, qtyOrdered);
    const { price: unitListPrice, priceDisplay: unitListPriceDisplay } = getUnitListPrice(pricing, qtyOrdered);
    const hasPriceSavings = unitNetPrice < unitListPrice;
    if (!language || !pricing || !hasPriceSavings) {
        return null;
    }

    const savingsAmount = Math.round((unitListPrice - unitNetPrice) * 100) / 100;
    const localizedSavingsAmount = getLocalizedCurrency({ amount: savingsAmount, language, currencySymbol });
    const savingsPercent = Math.round((savingsAmount / unitListPrice) * 100);

    /* Store Custom Code - Start */
    const regPriceText = (
        <>
            <Typography {...styles.regularPrice}>{translate("Regular Price: ")}</Typography>
            <Typography {...styles.priceStrikethrough}>{unitListPriceDisplay}</Typography>
        </>
    );
    let message: string | JSX.Element = "";
    if (showSavingsAmount && showSavingsPercent) {
        message = (
            <>
                {regPriceText}&nbsp;
                <Typography {...styles.saveMsg}>
                    {translate("Save {0} ({1}%)", localizedSavingsAmount, `${savingsPercent}`)}
                </Typography>
            </>
        );
    } else if (showSavingsPercent) {
        message = (
            <>
                {regPriceText}&nbsp;
                <Typography {...styles.saveMsg}>{translate("Save {0}%", `${savingsPercent}`)}</Typography>
            </>
        );
    } else if (showSavingsAmount) {
        message = (
            <>
                {regPriceText}&nbsp;
                <Typography {...styles.saveMsg}>{translate("Save {0}", localizedSavingsAmount)}</Typography>
            </>
        );
    } else {
        message = <>{regPriceText}</>;
    }
    /* Store Custom Code - End */

    return (
        <Typography {...otherProps} data-test-selector="productPriceSavingsMessage">
            {message}
        </Typography>
    );
};

export default connect(mapStateToProps)(ProductPriceSavingsMessage);
